import React from 'react';
import { Box, Typography, Card, CardContent, Tooltip, Container } from '@mui/material';
import RarityAvatar from '../common/RarityAvatar';
import { allAnimals } from '../../data/animalData';

const getAnimalUpgradeData = (animalType) => {
  const variants = allAnimals[animalType]?.variants || [];
  if (!variants.length) return [];

  // Get the rarities from the first variant
  const rarities = variants[0]?.rarity || [];

  return rarities.map((rarity, index, array) => {
    const nextRarity = array[index + 1];

    // For all rarities except the last one, we need upgrade requirements to reach the next level
    // The upgrade requirements for transitioning TO a rarity are stored in that rarity's upgradeRequirements
    if (!nextRarity) {
      // Last rarity - no upgrade needed to reach next level
      return {
        id: rarity.name ? rarity.name.toLowerCase().replace(/\s+/g, '_') : '',
        name: rarity.name || '',
        rarity: rarity.type || '',
        image: rarity.image || '',
        upgradeRequirements: null,
        nextUpgrade: null
      };
    }

    // Get upgrade requirements for transitioning TO the NEXT rarity from the current rarity
    // These requirements are stored in the NEXT rarity's upgradeRequirements
    const upgradeReqs = nextRarity.upgradeRequirements;

    return {
      id: rarity.name ? rarity.name.toLowerCase().replace(/\s+/g, '_') : '',
      name: rarity.name || '',
      rarity: rarity.type || '',
      image: rarity.image || '',
      upgradeRequirements: upgradeReqs,
      nextUpgrade: {
        target: nextRarity.name ? nextRarity.name.toLowerCase().replace(/\s+/g, '_') : '',
        requirements: [
          {
            type: 'animals',
            id: rarity.type ? rarity.type.toLowerCase() : '',
            amount: 2
          },
          {
            type: 'food',
            id: nextRarity.type ? `${animalType}_food_${nextRarity.type.toLowerCase()}` : '',
            rarity: nextRarity.type || '',
            amount: upgradeReqs?.food?.amount || 0
          },
          {
            type: 'cfb',
            id: 'cfb',
            amount: upgradeReqs?.cfb || 0
          }
        ].filter(req => req.amount > 0 && req.id)
      }
    };
  }).filter(animal => animal.name);
};

const getFoodImage = (animalType, rarity) => {
  const foodType = {
    'birds': 'egg',
    'cattle': 'milk',
    'fuzzies': 'fuzzies'
  }[animalType] || 'egg';
  
  return `/images/seeds/${foodType}/${rarity.toLowerCase()}/preview.png`;
};

const AnimalUpgradePath = ({ animalType = 'birds' }) => {
  const upgradeData = React.useMemo(() => getAnimalUpgradeData(animalType), [animalType]);
  const animalName = animalType.charAt(0).toUpperCase() + animalType.slice(1);
  
  const totalResources = React.useMemo(() => {
    return upgradeData.reduce((acc, animal, index, array) => {
      if (!animal.nextUpgrade) return acc;

      // The count represents how many of this animal we need at this level
      const currentCount = Math.pow(2, array.length - 1 - index);

      // For upgrading to the next level, we need to perform (currentCount / 2) merges
      // Each merge consumes 2 of current + requirements → produces 1 of next
      const mergesNeeded = currentCount / 2;

      animal.nextUpgrade.requirements.forEach(req => {
        if (req.type === 'food') {
          const rarity = req.rarity.toLowerCase();
          acc.food[rarity] = (acc.food[rarity] || 0) + (req.amount * mergesNeeded);
        } else if (req.type === 'cfb') {
          acc.cfb += req.amount * mergesNeeded;
        }
      });

      return acc;
    }, { food: {}, cfb: 0 });
  }, [upgradeData]);

  // Define common avatar styles
  const avatarStyles = {
    width: 40,
    height: 40,
    borderRadius: 1,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'transparent',
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
    }
  };

  const renderAnimalTier = (animal, count, isLast = false) => {
    const foodReq = animal.nextUpgrade?.requirements?.find(r => r.type === 'food');
    const cfbReq = animal.nextUpgrade?.requirements?.find(r => r.type === 'cfb');

    return (
      <React.Fragment key={animal.id}>
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            py: 2,
            borderBottom: !isLast ? '1px solid' : 'none',
            borderColor: 'divider'
          }}
        >
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(count, 8)}, 40px)`,
            gap: 1,
            width: 'fit-content',
            mx: 'auto',
            '& > *': {
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.1)'
              }
            }
          }}>
            {Array.from({ length: count }).map((_, i) => (
              <Tooltip 
                key={`${animal.id}-${i}`} 
                title={`${animal.name} (${animal.rarity})`} 
                arrow
              >
                <Box sx={{ width: '100%', height: '100%' }}>
                  <RarityAvatar
                    src={animal.image}
                    alt={animal.name}
                    size="md"
                    sx={{
                      width: '40px',
                      height: '40px',
                      '& img': {
                        width: '40px !important',
                        height: '40px !important',
                        objectFit: 'contain !important',
                        objectPosition: 'center',
                        display: 'block !important',
                        p: 0,
                        margin: 'auto',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                      },
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease-in-out'
                    }}
                  />
                </Box>
              </Tooltip>
            ))}
          </Box>

          {!isLast && (foodReq || cfbReq) && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              mb: 4,
              maxWidth: 1200,
              mx: 'auto',
              '&:hover': {
                transform: 'none !important'
              }
            }}>
              <Typography variant="body2" color="text.primary" sx={{ textAlign: 'center', mt: 1, fontWeight: 'bold' }}>
                {count}x {animal.name} → {count/2}x {upgradeData[upgradeData.indexOf(animal) + 1]?.name || ''} ({count/2} merges)
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
                mt: 1
              }}>
                {foodReq && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RarityAvatar
                      src={getFoodImage(animalType, foodReq.rarity)}
                      alt={`${foodReq.rarity} Food`}
                      size="md"
                      sx={{
                        ...avatarStyles,
                        width: '40px',
                        height: '40px',
                        border: 'none',
                        '& img': {
                          ...avatarStyles['& img'],
                          objectFit: 'contain',
                          padding: '2px'
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      x {foodReq.amount * (count / 2)}
                    </Typography>
                  </Box>
                )}
                {cfbReq && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RarityAvatar
                      src="/images/icon_CFB.svg"
                      alt="$CFB"
                      size="md"
                      sx={{
                        ...avatarStyles,
                        width: '40px',
                        height: '40px',
                        border: 'none',
                        '& img': {
                          ...avatarStyles['& img'],
                          objectFit: 'contain',
                          padding: '2px'
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                      x {cfbReq.amount * (count / 2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </React.Fragment>
    );
  };

  if (!upgradeData.length) return null;

  return (
    <Container>
      <Box sx={{
        mt: 4,
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        mb: 4,
        '&:hover': {
          transform: 'none',
          boxShadow: 'none'
        }
      }}>
      <Card
        elevation={0}
        component="div"
        sx={{
          bgcolor: 'transparent',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          width: '100%',
          position: 'relative',
          top: 0,
          left: 0,
          transform: 'none',
          transition: 'none',
          boxShadow: 'none',
          outline: 'none',
          '&:hover': {
            transform: 'none',
            boxShadow: 'none',
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            outline: 'none',
            top: 0,
            left: 0
          }
        }}
        tabIndex={-1}
      >
        <CardContent sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          '&:hover': {
            bgcolor: 'transparent'
          }
        }}>
          <Typography
            variant="h6"
            sx={{
              color: 'primary.main',
              mb: 2,
              textAlign: 'center',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
          >
            Upgrade Path Summary
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 'bold', 
                textAlign: 'center', 
                fontSize: '1rem', 
                mb: 1 
              }}
            >
              {animalName} Requirements:
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              p: 2
            }}>
              {upgradeData.map((animal, index, array) => {
                const count = Math.pow(2, array.length - 1 - index);
                const isLast = index === array.length - 1;
                return renderAnimalTier(animal, count, isLast);
              })}
            </Box>
            <Box sx={{ 
              mt: 2, 
              pt: 2, 
              borderTop: '1px dashed', 
              borderColor: 'divider',
              textAlign: 'center'
            }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 'bold', 
                fontSize: '1rem', 
                mb: 1.5, 
                color: 'text.primary',
              }}>
                Total Resources Needed:
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: '100%',
                '& > *': {
                  flex: 1
                },
                '@media (min-width: 600px)': {
                  flexDirection: 'row',
                  gap: 2
                }
              }}>
                {/* Food by Rarity */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'none',
                  '&:hover': {
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}>
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    width: '100%'
                  }}>
                    {Object.entries(totalResources.food).map(([rarity, amount]) => (
                      amount > 0 && (
                        <Box key={rarity} sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 1
                        }}>
                          <RarityAvatar
                            src={getFoodImage(animalType, rarity.charAt(0).toUpperCase() + rarity.slice(1))}
                            alt={`${rarity} Food`}
                            size="sm"
                            sx={{
                              ...avatarStyles,
                              width: '40px',
                              height: '40px',
                              border: 'none',
                              '& img': {
                                ...avatarStyles['& img'],
                                objectFit: 'contain'
                              }
                            }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}>
                            x{Math.round(amount).toLocaleString()}
                          </Typography>
                        </Box>
                      )
                    ))}
                  </Box>
                </Box>

                {/* CFB */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'none',
                  '&:hover': {
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    border: 'none',
                    '& img': {
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      border: 'none',
                      boxShadow: 'none'
                    }
                  }}>
                    <img
                      src="/images/icon_CFB.svg"
                      alt="$CFB Token"
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                  <Typography variant="body1" color="success.main" sx={{
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}>
                    x{totalResources.cfb.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Note section */}
      <Box sx={{ 
        mt: 2,
        mb: 4,
        p: 2, 
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        maxWidth: 1200,
        mx: 'auto'
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ 
          fontSize: '0.85rem', 
          fontStyle: 'italic',
          textAlign: 'center',
          lineHeight: 1.4
        }}>
          <strong>Note:</strong> This calculation includes all intermediate animals and resources needed to upgrade from {animalName} Common to Legendary in one continuous path. Each merge consumes 2 animals of the current rarity + requirements to produce 1 animal of the next rarity.
        </Typography>
      </Box>
    </Box>
    </Container>
  );
};

export default AnimalUpgradePath;
